import { AdvancedMarker, APIProvider, Map, MapCameraChangedEvent } from '@vis.gl/react-google-maps';
import { LoadingOverlay } from 'components/Basic/LoadingOverlay/LoadingOverlay';
import { MarkerIcon } from 'components/Basic/Map/MarkerIcon';
import { PigeonMap } from 'components/Basic/Map/PigeonMap/PigeonMap';
import { usePigeonMap } from 'components/Basic/Map/PigeonMap/usePigeonMap';
import { PointMapCard } from 'components/Basic/Map/PointMapCard';
import { useMapContext } from 'context/Map/useMapContext';
import { MapProps } from 'types/map';

const MAP_IDENTIFIER = 'map-provider-google';

export const GoogleMap: FC<MapProps> = ({
    children,
    markers,
    clickOnMarkerHandler,
    shouldShowSpinner,
    clusterRadius,
    shouldChangeFitToBounds = true,
    minimumFitToBoundsZoom,
}) => {
    const [{ mapProvider }, dispatch] = useMapContext();

    const {
        handleBoundsChanged: dispatchBoundsChanged,
        onPointClickHandler,
        clusters,
        center,
        zoom,
        bounds,
        activePoint,
        activeMarkerUuid,
        isActivePointValidForPopup,
        mapContainerRef,
    } = usePigeonMap({
        markers,
        clickOnMarkerHandler,
        shouldChangeFitToBounds,
        minimumFitToBoundsZoom,
        clusterRadius,
    });

    const googleBounds: google.maps.LatLngBoundsLiteral = {
        north: bounds[2],
        south: bounds[3],
        east: bounds[0],
        west: bounds[1],
    };

    const handleBoundsChanged = (event: MapCameraChangedEvent) => {
        const newCenter = event.detail.center;
        const newZoom = event.detail.zoom;
        const newBounds = event.detail.bounds;

        dispatchBoundsChanged({
            center: [newCenter.lat, newCenter.lng],
            zoom: newZoom,
            bounds: {
                ne: [newBounds.north, newBounds.east],
                sw: [newBounds.south, newBounds.west],
            },
            initial: false,
        });
    };

    if (!mapProvider?.apiKey || !mapProvider.mapId) {
        return (
            <PigeonMap
                clickOnMarkerHandler={clickOnMarkerHandler}
                clusterRadius={clusterRadius}
                markers={markers}
                minimumFitToBoundsZoom={minimumFitToBoundsZoom}
                shouldChangeFitToBounds={shouldChangeFitToBounds}
                shouldShowSpinner={shouldShowSpinner}
            >
                {children}
            </PigeonMap>
        );
    }

    return (
        <div className="h-full w-full" data-testid={MAP_IDENTIFIER} ref={mapContainerRef}>
            <APIProvider apiKey={mapProvider.apiKey}>
                <Map
                    disableDoubleClickZoom
                    zoomControl
                    center={center}
                    defaultBounds={googleBounds}
                    fullscreenControl={false}
                    mapId={mapProvider.mapId}
                    mapTypeControl={false}
                    streetViewControl={false}
                    zoom={zoom}
                    zoomControlOptions={{ position: 1 }}
                    onBoundsChanged={handleBoundsChanged}
                >
                    {clusters.map((point) => {
                        const properties = point.properties;
                        const [lng, lat] = point.geometry.coordinates;
                        const isCluster = 'cluster' in properties && properties.cluster;
                        const pointCount = isCluster ? properties.point_count : 1;

                        return (
                            <AdvancedMarker
                                key={isCluster ? `cluster-${point.id}` : properties.identifier}
                                position={{ lat: lat, lng: lng }}
                                onClick={() => onPointClickHandler(properties.identifier, Number(point.id), isCluster)}
                            >
                                <MarkerIcon
                                    activeMarkerUuid={activeMarkerUuid || null}
                                    isCluster={properties.cluster}
                                    point={properties}
                                    totalPoints={clusters.length}
                                    totalPointsInCluster={pointCount}
                                />
                            </AdvancedMarker>
                        );
                    })}
                    {activePoint && isActivePointValidForPopup && (
                        <AdvancedMarker
                            className="z-above -translate-y-3"
                            position={{
                                lat: activePoint.geometry.coordinates[1],
                                lng: activePoint.geometry.coordinates[0],
                            }}
                            onClick={() => dispatch({ type: 'setActiveMarkerUuid', payload: undefined })}
                        >
                            <PointMapCard activeMarker={activePoint.properties} />
                        </AdvancedMarker>
                    )}
                    {children}
                </Map>
                {shouldShowSpinner && <LoadingOverlay className="h-12 w-12" />}
            </APIProvider>
        </div>
    );
};
